/**
 * Validação final da rota (módulo puro).
 *
 * Última barreira antes de apresentar/guardar: cada paragem tem de respeitar
 * a localidade explícita pedida, a categoria pedida (só restaurantes) e a
 * cozinha explícita. Em caso de dúvida — sem prova de tipo ou sem prova de
 * localidade — a paragem é REJEITADA. Nunca se alarga a zona em silêncio.
 */

import type { CuisineIntent } from "./cuisine-intent";
import { isMarketLikePlace, placeMatchesCuisine } from "./cuisine-filter";

export type FinalPlaceEvidence = {
  name?: string | null;
  primaryType?: string | null;
  types?: string[] | null;
  /** Nomes administrativos do local (locality, postal_town, admin 2/3). */
  localityNames?: string[] | null;
  /** Coordenadas dentro da área geocodificada da localidade pedida. */
  insideRequestedArea?: boolean | null;
  /**
   * Prova geométrica máxima: coordenada dentro da ExactLocalityArea
   * geocodificada pelo fornecedor para a localidade pedida. Quando true, a
   * identidade/geometria resolvida pelo fornecedor decide — nunca comparação
   * textual de nomes (exónimos: "Zurique" vs "Zürich").
   */
  insideExactLocalityBounds?: boolean | null;
};

export type FinalValidationOptions = {
  /** Localidade explícita pedida ("Leça da Palmeira"). */
  requestedLocality?: string | null;
  /** Aliases devolvidos pelo geocoder para essa localidade. */
  localityAliases?: string[];
  /** O pedido é exclusivamente de restaurantes. */
  restaurantsOnly?: boolean;
  /** Cozinha explícita pedida. */
  cuisine?: CuisineIntent | null;
  /** O utilizador pediu explicitamente um mercado. */
  allowMarket?: boolean;
};

export type RejectionReason = "locality" | "not_restaurant" | "cuisine" | "unverified";

const fold = (value: string) =>
  (value ?? "").normalize("NFD").replace(/[\u0300-\u036f]/gu, "").toLowerCase().trim();

/** Tipos que provam um restaurante do Google Places. */
const RESTAURANT_TYPES = new Set([
  "restaurant",
  "fine_dining_restaurant",
  "meal_takeaway",
  "meal_delivery",
  "steak_house",
  "pizza_restaurant",
  "seafood_restaurant",
  "sushi_restaurant",
  "ramen_restaurant",
  "japanese_restaurant",
  "chinese_restaurant",
  "italian_restaurant",
  "indian_restaurant",
  "thai_restaurant",
  "mexican_restaurant",
  "korean_restaurant",
  "vietnamese_restaurant",
  "spanish_restaurant",
  "french_restaurant",
  "greek_restaurant",
  "turkish_restaurant",
  "lebanese_restaurant",
  "brazilian_restaurant",
  "portuguese_restaurant",
  "mediterranean_restaurant",
  "asian_restaurant",
  "american_restaurant",
  "hamburger_restaurant",
  "vegan_restaurant",
  "vegetarian_restaurant",
  "barbecue_restaurant",
  "buffet_restaurant",
  "brunch_restaurant",
  "breakfast_restaurant",
]);

/** Tipos que nunca podem entrar numa lista só de restaurantes. */
const NON_RESTAURANT_TYPES = new Set([
  "beach",
  "church",
  "place_of_worship",
  "hindu_temple",
  "mosque",
  "synagogue",
  "tourist_attraction",
  "museum",
  "art_gallery",
  "park",
  "national_park",
  "hiking_area",
  "historical_landmark",
  "monument",
  "natural_feature",
  "marina",
  "zoo",
  "aquarium",
  "amusement_park",
  "shopping_mall",
  "market",
  "food_court",
  "grocery_store",
  "supermarket",
]);

const typeList = (place: FinalPlaceEvidence): string[] =>
  [place.primaryType, ...(place.types ?? [])].filter(Boolean).map((t) => fold(String(t)));

/** É um restaurante provado pelo Places? */
export function isVerifiedRestaurant(place: FinalPlaceEvidence): boolean {
  const types = typeList(place);
  if (!types.length) return false;
  if (isMarketLikePlace(place)) return false;
  if (types.some((t) => NON_RESTAURANT_TYPES.has(t))) return false;
  return types.some((t) => RESTAURANT_TYPES.has(t) || t.endsWith("_restaurant"));
}

const ATTRACTION_WORDS =
  /\b(atrac|atrat|attraction|sight|monument|museu|museum|igreja|church|praia|beach|miradouro|viewpoint|parque|park|jardim|garden|castelo|castle|passeio|visitar|ver\b|pontos? de interesse|point of interest|landmark)/u;

const RESTAURANT_WORDS =
  /\b(restaurante|restaurantes|restaurant|restaurants|jantar|almoc|comer|dine|dinner|lunch|eat|gastronom)/u;

/** O pedido menciona atrações/pontos de interesse? */
export function mentionsAttractions(text: string): boolean {
  return ATTRACTION_WORDS.test(fold(text ?? ""));
}

/**
 * O pedido é exclusivamente de restaurantes? Pedidos que combinam atrações e
 * restaurantes mantêm o comportamento normal da Routify.
 */
export function detectRestaurantsOnlyRequest(text: string): boolean {
  const t = fold(text ?? "");
  if (!t) return false;
  if (!RESTAURANT_WORDS.test(t)) return false;
  return !ATTRACTION_WORDS.test(t);
}

/**
 * Nomes de freguesias agregadas ("União das Freguesias de Matosinhos e Leça da
 * Palmeira") cobrem VÁRIAS localidades: contêm o nome pedido mas não provam que
 * o local lá está. Só contam quando são exatamente a localidade pedida.
 */
const MERGED_PARISH_RE = /\b(uniao das freguesias|uniao de freguesias|union of parishes)\b/;

function localityNameMatches(candidate: string, target: string): boolean {
  const a = fold(candidate);
  const b = fold(target);
  if (!a || !b) return false;
  if (a === b) return true;
  if (MERGED_PARISH_RE.test(a) || MERGED_PARISH_RE.test(b)) return false;
  return b.length >= 5 && (a.includes(b) || b.includes(a));
}

/**
 * A paragem está na localidade pedida? A prova usa a identidade e a
 * geometria resolvidas pelo fornecedor — nunca comparação textual quando
 * existe prova geométrica da ExactLocalityArea. Sem essa prova, exige
 * coordenadas dentro da área E correspondência dos addressComponents com a
 * localidade ou um dos aliases do geocoder. Dúvida ou conflito ⇒ false.
 */
export function matchesRequestedLocality(
  place: FinalPlaceEvidence,
  requestedLocality: string,
  aliases: string[] = [],
): boolean {
  // Prova geométrica máxima: dentro da ExactLocalityArea geocodificada pelo
  // fornecedor para a localidade pedida. Cobre exónimos ("Zurique" escrito
  // pela pessoa vs "Zürich" devolvido pelo fornecedor).
  if (place.insideExactLocalityBounds === true) return true;
  if (place.insideRequestedArea !== true) return false;
  const names = (place.localityNames ?? []).filter(Boolean) as string[];
  if (!names.length) return false;
  // Defesa: um alias só vale se for variante do nome pedido. Nomes de áreas
  // administrativas maiores (concelho, distrito) não autorizam locais de fora.
  const safeAliases = aliases.filter((alias) => localityNameMatches(alias, requestedLocality));
  const targets = [requestedLocality, ...safeAliases].filter(Boolean);
  return names.some((name) => targets.some((target) => localityNameMatches(name, target)));

}


export function validateFinalPlace(
  place: FinalPlaceEvidence,
  opts: FinalValidationOptions,
): { ok: true } | { ok: false; reason: RejectionReason } {
  if (opts.requestedLocality) {
    if (!matchesRequestedLocality(place, opts.requestedLocality, opts.localityAliases ?? [])) {
      return { ok: false, reason: "locality" };
    }
  }
  if (opts.restaurantsOnly || opts.cuisine) {
    if (!typeList(place).length) return { ok: false, reason: "unverified" };
  }
  if (opts.restaurantsOnly && !isVerifiedRestaurant(place)) {
    return { ok: false, reason: "not_restaurant" };
  }
  if (opts.cuisine && !placeMatchesCuisine(place, opts.cuisine, { allowMarket: opts.allowMarket })) {
    return { ok: false, reason: "cuisine" };
  }
  return { ok: true };
}

export function validateFinalPlaces<T>(
  items: T[],
  evidenceOf: (item: T) => FinalPlaceEvidence,
  opts: FinalValidationOptions,
): { kept: T[]; rejected: Array<{ item: T; reason: RejectionReason }> } {
  const kept: T[] = [];
  const rejected: Array<{ item: T; reason: RejectionReason }> = [];
  for (const item of items) {
    const result = validateFinalPlace(evidenceOf(item), opts);
    if (result.ok) kept.push(item);
    else rejected.push({ item, reason: result.reason });
  }
  return { kept, rejected };
}

/**
 * Localidade explícita escrita no fim do pedido ("... em Leça da Palmeira").
 * Só aceita nomes próprios; devolve null quando não há menção clara, para o
 * fluxo normal continuar a perguntar em vez de assumir uma zona.
 */
const REQUEST_LOCALITY_RE =
  /\b(?:em|no|na|in|at|en|à|a)\s+(\p{Lu}[\p{L}'’\-]+(?:\s+(?:d[aeoi]s?|de|do|da|del|della|of|the|von|van)\s+\p{L}[\p{L}'’\-]+|\s+\p{Lu}[\p{L}'’\-]+)*)/gu;

export function extractRequestLocality(text: string): string | null {
  // Corta durações e cauda irrelevante ("... 1 dia", "... hoje") antes de ler.
  const cleaned = (text ?? "")
    .replace(/\s*\d{1,3}\s*(?:h|horas?|hours?|dias?|days?)\b.*$/iu, "")
    .trim();
  const matches = [...cleaned.matchAll(REQUEST_LOCALITY_RE)];
  const value = matches.at(-1)?.[1]?.trim();
  return value && value.length >= 2 && value.length <= 80 ? value : null;
}
